import React, { Fragment, useState } from "react"
import { IconMoodEmpty, IconMoodSad, IconMoodSmile } from "@tabler/icons-react"
import { Radio, RadioGroup } from "@headlessui/react"
import { useMutation } from "@apollo/client/react"
import { Formik } from "formik"
import { toast } from "sonner"

import Button from "@love-irish/core-button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@love-irish/ui-components/dialog"
import { Label } from "@love-irish/ui-components/label"

import { Textarea } from "@love-irish/ui-components/textarea"
import Tooltip from "design_system/core/components/tooltip"
import analytics from "@love-irish/analytics"
import { ANALYTICS_EVENT_NAMES } from "@love-irish/analytics/events"

import mutation from "./sendFeedback.graphql"

const emojis = [
  { name: "Great", value: "😊", icon: IconMoodSmile },
  { name: "Neutral", value: "😐", icon: IconMoodEmpty },
  { name: "Bad", value: "😞", icon: IconMoodSad },
]

const DefaultTrigger = (
  <Button variant="outline" size="xs" color="secondary">
    ❤️ Give Feedback
  </Button>
)

interface FeedbackModalProps {
  trigger?: React.ReactNode
  /** Custom title for the modal dialog */
  title?: React.ReactNode
  /** Custom description text below the title */
  description?: React.ReactNode
}

export function FeedbackModal({
  trigger = DefaultTrigger,
  title,
  description,
}: FeedbackModalProps) {
  const [open, setOpen] = useState(false)

  const [sendFeedback, { loading }] = useMutation(mutation)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[425px] bg-white">
        <DialogHeader>
          <DialogTitle className="mb-3">
            {title ?? "Share your feedback with us ❤️"}
          </DialogTitle>
          <DialogDescription>
            {description ?? (
              <>
                We are always looking for ways to improve this site.{" "}
                <strong>
                  If you have any suggestions or ideas for what we can do
                  better, please let us know.
                </strong>{" "}
                Remember we are a small team so keep it constructive.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <Formik
          initialValues={{
            message: "",
            mood: "",
          }}
          validate={(values) => {
            const errors: { message?: string } = {}

            if (!values.message.trim()) {
              errors.message = "Feedback message is required"
            } else if (values.message.trim().length < 10) {
              errors.message = "Feedback must be at least 10 characters"
            } else if (values.message.length > 1000) {
              errors.message = "Feedback must be less than 1000 characters"
            }

            return errors
          }}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            try {
              const result = await sendFeedback({
                variables: {
                  message: values.message.trim(),
                  mood: values.mood || null,
                },
              })

              if (result.data?.sendFeedback?.success) {
                // Track successful feedback submission (Ahoy only - message is sensitive)
                analytics.track(
                  ANALYTICS_EVENT_NAMES.FEEDBACK_SUBMITTED,
                  {
                    mood: values.mood || null,
                    message: values.message.trim(),
                    message_length: values.message.trim().length,
                    has_mood: !!values.mood,
                    path: window.location.pathname,
                  },
                  { services: ["ahoy"] }
                )

                toast.success(
                  "Thank you for your feedback! We'll review it shortly. ❤️"
                )
                setOpen(false)
                resetForm()
              } else if (result.data?.sendFeedback?.errors?.length > 0) {
                const errorMessages = result.data.sendFeedback.errors
                  .map((err) => err.message)
                  .join(", ")
                toast.error(errorMessages)
              } else {
                toast.error("There was a problem sending your feedback")
              }
            } catch (err) {
              console.error("Failed to send feedback:", err)
              toast.error("Failed to send feedback. Please try again later.")
            } finally {
              setSubmitting(false)
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit}>
              <div className="grid gap-4 py-4">
                <Label htmlFor="feedbackMessage">Feedback *</Label>
                {errors.message && touched.message && (
                  <div className="text-error-500 text-sm -mt-2">
                    {errors.message}
                  </div>
                )}
                <Textarea
                  rows={5}
                  placeholder="Add your feedback for how we can improve or any issues you found."
                  id="feedbackMessage"
                  name="message"
                  value={values.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={loading || isSubmitting}
                />
              </div>

              <div>
                <Label id="select-feedback-mood">
                  Rate your overall experience with our site
                </Label>

                <div>
                  <RadioGroup
                    value={values.mood}
                    onChange={(value) => setFieldValue("mood", value)}
                    aria-label="Mood picker"
                    className="flex gap-3 mt-3"
                  >
                    {emojis.map(({ icon: Icon = Fragment, ...emoji }) => (
                      <Fragment key={emoji.name}>
                        <Tooltip title={emoji.name} className="z-50">
                          <Radio
                            aria-describedby="select-feedback-mood"
                            as={Button}
                            variant="outline"
                            size="sm"
                            value={emoji.value}
                            disabled={loading || isSubmitting}
                            className="group text-warning data-checked:bg-success-200 data-checked:border-success-500! data-checked:text-success-700"
                          >
                            <span className="text-xl">
                              <Icon />
                            </span>
                          </Radio>
                        </Tooltip>
                      </Fragment>
                    ))}
                  </RadioGroup>
                </div>
              </div>

              <DialogFooter>
                <Button type="submit" disabled={loading || isSubmitting}>
                  {loading || isSubmitting ? "Sending..." : "Send feedback"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default FeedbackModal
